<template>
  <div v-if="reportStore.isLoading" class="flex justify-center items-center h-64">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>

  <div v-else-if="reportStore.currentReport" class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="bg-white shadow rounded-lg mb-6">
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">
              Resultados de Laboratorios - {{ reportStore.currentReport.patientName || 'Sin nombre' }}
            </h1>
            <p class="text-gray-600">
              Fecha de muestra: {{ formatDate(reportStore.currentReport.sampleDate) }}
            </p>
          </div>
          <div class="flex space-x-3">
            <button
              @click="exportToPDF"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Exportar PDF
            </button>
            <button
              @click="goBack"
              class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Patient Info -->
    <div v-if="reportStore.currentReport.patientName" class="bg-white shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Información del Paciente</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="text-sm font-medium text-gray-500">Nombre</label>
            <p class="text-gray-900">{{ reportStore.currentReport.patientName }}</p>
          </div>
          <div v-if="reportStore.currentReport.patientAge">
            <label class="text-sm font-medium text-gray-500">Edad</label>
            <p class="text-gray-900">{{ reportStore.currentReport.patientAge }} años</p>
          </div>
          <div v-if="reportStore.currentReport.patientSex">
            <label class="text-sm font-medium text-gray-500">Sexo</label>
            <p class="text-gray-900">{{ reportStore.currentReport.patientSex }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Lab Results -->
    <div v-if="reportStore.currentReport.labResults" class="bg-white shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Resultados de Laboratorio</h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parámetro
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unidad
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rango de Referencia
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="(result, index) in reportStore.currentReport.labResults.resultados"
                :key="index"
                class="hover:bg-gray-50"
              >
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ result.parametro }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ result.valor }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ result.unidad }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ result.rango_referencia }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="getValueStatusClass(result.valor, result.rango_referencia)"
                    class="px-2 py-1 text-xs font-medium rounded-full"
                  >
                    {{ getValueStatus(result.valor, result.rango_referencia) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Clinical Analysis -->
    <div v-if="reportStore.currentReport.clinicalAnalysis" class="bg-white shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Análisis Clínico</h2>
        
        <div v-if="(reportStore.currentReport.clinicalAnalysis as any)?.summary" class="mb-6">
          <h3 class="text-md font-medium text-gray-900 mb-2">Resumen</h3>
          <p class="text-gray-700">{{ (reportStore.currentReport.clinicalAnalysis as any).summary }}</p>
        </div>

        <div v-if="reportStore.currentReport.clinicalAnalysis.interpretation" class="mb-6">
          <h3 class="text-md font-medium text-gray-900 mb-2">Interpretación</h3>
          <p class="text-gray-700">{{ reportStore.currentReport.clinicalAnalysis.interpretation }}</p>
        </div>

        <div v-if="(reportStore.currentReport.clinicalAnalysis as any)?.actions" class="mb-6">
          <h3 class="text-md font-medium text-gray-900 mb-2">Recomendaciones</h3>
          <ul class="list-disc list-inside space-y-1">
            <li
              v-for="(action, index) in (reportStore.currentReport.clinicalAnalysis as any).actions"
              :key="index"
              class="text-gray-700"
            >
              {{ action }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Audit Results -->
    <div v-if="reportStore.currentReport.auditResults" class="bg-white shadow rounded-lg">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Auditoría de Calidad</h2>
        
        <div v-if="reportStore.currentReport.auditResults.quality_score" class="mb-4">
          <h3 class="text-md font-medium text-gray-900 mb-2">Puntuación de Calidad</h3>
          <div class="flex items-center">
            <div class="w-full bg-gray-200 rounded-full h-2.5">
              <div
                class="bg-blue-600 h-2.5 rounded-full"
                :style="{ width: `${reportStore.currentReport.auditResults.quality_score}%` }"
              ></div>
            </div>
            <span class="ml-3 text-sm font-medium text-gray-900">
              {{ reportStore.currentReport.auditResults.quality_score }}%
            </span>
          </div>
        </div>

        <div v-if="(reportStore.currentReport.auditResults as any)?.recommendations" class="mb-4">
          <h3 class="text-md font-medium text-gray-900 mb-2">Recomendaciones de Mejora</h3>
          <ul class="list-disc list-inside space-y-1">
            <li
              v-for="(recommendation, index) in (reportStore.currentReport.auditResults as any).recommendations"
              :key="index"
              class="text-gray-700"
            >
              {{ recommendation }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="text-center py-12">
    <div class="text-gray-500">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No se encontró el reporte</h3>
      <p class="mt-1 text-sm text-gray-500">El reporte solicitado no existe o no está disponible.</p>
      <div class="mt-6">
        <button
          @click="goBack"
          class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Volver al inicio
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useReportStore } from '../stores/reportStore'
import { useToast } from 'vue-toastification'

const route = useRoute()
const router = useRouter()
const reportStore = useReportStore()
const toast = useToast()

onMounted(async () => {
  const reportId = route.params.reportId as string
  if (reportId) {
    try {
      await reportStore.getReport(reportId)
    } catch (error) {
      toast.error('Error al cargar el reporte')
      console.error('Error loading report:', error)
    }
  }
})

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const getValueStatus = (value: any, reference: string): string => {
  if (typeof value !== 'number') return 'N/A'
  
  const [min, max] = reference.split('-').map(Number)
  if (value < min) return 'Bajo'
  if (value > max) return 'Alto'
  return 'Normal'
}

const getValueStatusClass = (value: any, reference: string): string => {
  const status = getValueStatus(value, reference)
  switch (status) {
    case 'Normal':
      return 'bg-green-100 text-green-800'
    case 'Bajo':
    case 'Alto':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const exportToPDF = () => {
  // TODO: Implement PDF export
  toast.info('Función de exportación en desarrollo')
}

const goBack = () => {
  router.push('/')
}
</script>
